/*
feedMain.js
Main entry point for feed functionality.
Sets up configuration and initializes all modules.
*/

import { initFeedToggle } from './feedToggle.js';
import { initUserSearch } from './userSearch.js';
import { initSwipe } from './swipe.js';
import { initReactionHandlers } from './reaction.js';
import { initCommentHandlers } from './comment.js';
import { initHighlightHandlers } from './highlight.js';
import { initModalOptions } from './modalOptions.js';
import { debounce } from './utilities.js';
import { initProfileLinks } from './profileLinks.js';
import { initInviteFriends } from './inviteFriends.js';

document.addEventListener('DOMContentLoaded', () => {
  const feedContainer = document.getElementById('feed');
  if (!feedContainer) return;

  const loader = document.getElementById('loader');
  const csrfMeta = document.querySelector('meta[name="csrf-token"]');
  const csrfToken = csrfMeta ? csrfMeta.getAttribute('content') : '';

  // Get current user info and feed configuration from the DOM.
  const currentUserId = feedContainer.getAttribute('data-current-user-id') || null;
  const currentUserUsername = feedContainer.getAttribute('data-username') || '';
  const currentFeedType = feedContainer.getAttribute('data-feed-type') || 'all';
  const isAdmin = (feedContainer.getAttribute('data-is-admin') === 'true');

  const urlParams = new URLSearchParams(window.location.search);
  const currentSortMode = urlParams.get('sort') || 'new';

  const isAuthenticated = Boolean(currentUserId && currentUserId.trim() !== "");

  // Shared config object for all modules
  const config = {
    feedContainer,
    loader,
    csrfToken,
    currentUserId,
    currentUserUsername,
    currentFeedType,
    isAdmin,
    currentSortMode,
    isAuthenticated,
  };

  // Initialize modules
  initFeedToggle(config);
  initUserSearch(config);
  initSwipe(config);
  initReactionHandlers(config);
  initCommentHandlers(config);  // Called only once here
  initHighlightHandlers(config);
  initModalOptions(config);
  initProfileLinks(config); // New: Handles clicks on user profile links for follow/redirect logic
  initInviteFriends(config); // New: Handles invite friends button clicks

  // ---------------------------
  // AUTH MODAL LOGIC
  // ---------------------------
  let isModalShown = false;
  if (!isAuthenticated && currentFeedType === 'follow') {
    // If the user is not authenticated and on the "Following" tab, show modal immediately.
    $('#authModal').modal({
      backdrop: 'static',
      keyboard: false,
    });
    isModalShown = true;
  } else if (!isAuthenticated) {
    // If not authenticated (on Discover), show modal after scrolling one viewport height.
    window.addEventListener('scroll', debounce(checkScrollForModal, 200));
  }

  function checkScrollForModal() {
    const scrollThreshold = window.innerHeight; // One page height
    if (!isModalShown && window.scrollY > scrollThreshold) {
      $('#authModal').modal({
        backdrop: 'static',
        keyboard: false,
      });
      isModalShown = true;
    }
  }

  // ---------------------------
  // INFINITE SCROLL (Load on Demand)
  // Only allow infinite scroll if the user is authenticated.
  // ---------------------------
  let infiniteScrollLoaded = false;
  function maybeLoadInfiniteScroll() {
    if (!isAuthenticated) return;
    if (
      !infiniteScrollLoaded &&
      window.scrollY + window.innerHeight >= document.body.scrollHeight - 600
    ) {
      infiniteScrollLoaded = true;
      import('./infiniteScroll.js').then(({ initInfiniteScroll }) => {
        initInfiniteScroll(config);
      });
    }
  }
  window.addEventListener('scroll', maybeLoadInfiniteScroll, { passive: true });

  // ---------------------------
  // BACK-TO-TOP BUTTON LOGIC
  // ---------------------------
  const backToTopBtn = document.getElementById('backToTopBtn');
  if (backToTopBtn) {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 600) {
        backToTopBtn.classList.remove('hidden');
      } else {
        backToTopBtn.classList.add('hidden');
      }
    });
    backToTopBtn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // ---------------------------
  // AUTH MODAL BUTTON LOGIC
  // ---------------------------
  const loginBtn = document.getElementById('loginBtn');
  const signupBtn = document.getElementById('signupBtn');

  if (loginBtn) {
    loginBtn.addEventListener('click', () => {
      window.location.href = loginBtn.dataset.url;
    });
  }

  if (signupBtn) {
    signupBtn.addEventListener('click', () => {
      window.location.href = signupBtn.dataset.url;
    });
  }
});
